import type { TeamIntegration } from '@rhythmic/db/types'
import type { UpdateTeamIntegrationPayload } from '@rhythmic/types'
import { UpdateTeamIntegrationSchema } from '@rhythmic/types'
import { useRhythmicLogger } from '#imports'
import { getTeamIntegrationsCollection } from '../../contexts/getCollections'

const logger = useRhythmicLogger('useTeamIntegrationsContext:updateTeamIntegration')

export interface UpdateTeamIntegrationContext {
  teamId: string
}

function validateParams(
  integrationId: string,
  payload: UpdateTeamIntegrationPayload,
  context: UpdateTeamIntegrationContext,
) {
  const { teamId } = context

  if (!teamId) {
    const error = new Error('Team context is not ready')
    logger.error('Unable to update team integration: missing team identifier')
    throw error
  }

  if (!integrationId) {
    const error = new Error('Integration identifier is required')
    logger.error('Unable to update team integration: missing integration identifier')
    throw error
  }

  const parsed = UpdateTeamIntegrationSchema.parse(payload)

  const changes = Object.fromEntries(
    Object.entries(parsed).filter(([, value]) => value !== undefined),
  ) as UpdateTeamIntegrationPayload

  if (Object.keys(changes).length === 0) {
    const error = new Error('No changes provided to update team integration')
    logger.warn('Team integration update aborted: no changes detected', {
      teamId,
      integrationId,
    })
    throw error
  }

  return {
    teamId,
    integrationId,
    changes,
  }
}

export default async function handleUpdateTeamIntegration(
  integrationId: string,
  payload: UpdateTeamIntegrationPayload,
  context: UpdateTeamIntegrationContext,
): Promise<TeamIntegration | null> {
  try {
    const { teamId, changes } = validateParams(integrationId, payload, context)
    const collection = getTeamIntegrationsCollection(teamId)

    const result = await collection.update(integrationId, (draft: TeamIntegration) => {
      if (changes.integration_endpoint !== undefined) draft.integration_endpoint = changes.integration_endpoint ?? null

      if (changes.access_token !== undefined) draft.access_token = changes.access_token ?? null

      if (changes.configuration_details !== undefined)
        draft.configuration_details = changes.configuration_details ?? null

      draft.updated_at = new Date()
    })

    const updated = Array.isArray(result) ? result[0] : result

    logger.info('Team integration updated via collection', {
      teamId,
      integrationId,
    })

    return updated ?? null
  } catch (error) {
    logger.error('updateTeamIntegration failed', {
      integrationId,
      payload,
      error,
    })
    if (error instanceof Error) throw error
    throw new Error('Failed to update team integration')
  }
}
